import React, { useState, useMemo, useEffect } from "react";
import AdminLayout from "../../Layout/index.jsx";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import { IoMdArrowRoundBack } from "react-icons/io";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, gql, useMutation } from "@apollo/client";
import { HiCalendar } from "react-icons/hi";
import swal from "sweetalert";
import Loader from "../../../../components/Loader.jsx";
import ModalUI from "../../../Assignments/Components/ModalUI.jsx";

// Query 1: Get modules for a campus
const GET_MODULES_BY_CAMPUS = gql`
  query GetModulesByCampus($campusName: String!) {
    getModulesByCampus(campusName: $campusName) {
      moduleID
      enabled_dates
    }
  }
`;

// Query 2: Get all modules with details
const GET_ALL_MODULES = gql`
  query GetAllModules {
    getAllModules {
      _id
      name
      domain
      short_description
      start_date
      end_date
      daywise_plan {
        name
        questions
        createdAt
        updatedAt
      }
    }
  }
`;

// Mutation: Request deletion
const REQUEST_DELETION = gql`
  mutation RequestDeletion(
    $typeOfDeletion: String!
    $payload: JSON!
    $reasonForDeletion: String!
    $requestedBy: String!
    $title: String!
  ) {
    requestDeletion(
      typeOfDeletion: $typeOfDeletion
      payload: $payload
      reasonForDeletion: $reasonForDeletion
      requestedBy: $requestedBy
      title: $title
    ) {
      id
      statusOfApproval
    }
  }
`;

const ModuleListing = () => {
  const { campusName } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [reasonInput, setReasonInput] = useState("");
  const [Isload, setIsload] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);

  // Queries
  const {
    data: campusData,
    loading: campusLoading,
    error: campusError,
  } = useQuery(GET_MODULES_BY_CAMPUS, { variables: { campusName } });

  const {
    data: allModulesData,
    loading: allModulesLoading,
    error: allModulesError,
  } = useQuery(GET_ALL_MODULES);

  
   useEffect(()=>{
    setTimeout(()=>{
      setIsload(false);
    },2000);
  },[]);

  // Mutation
  const [requestDeletion] = useMutation(REQUEST_DELETION);

  const campusModules = campusData?.getModulesByCampus || [];
  const allModules = allModulesData?.getAllModules || [];

  // Group by moduleID (deduplicate) and merge cluster assignment info
  const moduleClusterMap = useMemo(() => {
    const map = new Map();
    campusModules.forEach((mod) => {
      if (!map.has(mod.moduleID)) {
        map.set(mod.moduleID, {
          ...mod,
          clusterAssignments: [mod],
        });
      } else {
        map.get(mod.moduleID).clusterAssignments.push(mod);
      }
    });
    return map;
  }, [campusModules]);

  const uniqueModules = useMemo(() => {
    return Array.from(moduleClusterMap.values());
  }, [moduleClusterMap]);

  const modulesWithDetails = uniqueModules.map((mod) => {
    const details = allModules.find((m) => m._id === mod.moduleID);
    const allEnabled = mod.clusterAssignments.map(a => a.enabled_dates || []);
    const mergedEnabled = allEnabled.reduce((longest, arr) =>
      arr.length > longest.length ? arr : longest
    , []);
    return {
      ...mod,
      enabled_dates: mergedEnabled,
      details,
      clusterCount: mod.clusterAssignments.length,
    };
  });

  const filteredModules = useMemo(() => {
    return modulesWithDetails.filter((module) =>
      module.details?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [modulesWithDetails, searchTerm]);

  const handleRequestDeletion = async (module) => {
    const reason = prompt("Enter reason for deletion:");
    if (!reason) return;

    try {
      const payload = {
        operationName: "DeleteModule",
        variables: { id: module.moduleID },
        query: `
          mutation DeleteModule($id: String!) {
            deleteModule(id: $id)
          }
        `,
      };

      await requestDeletion({
        variables: {
          typeOfDeletion: "MODULE",
          payload,
          reasonForDeletion: reason,
          requestedBy: "AdminUser", // Replace with actual logged-in user
          title: module.details?.name || "Unnamed Module",
        },
      });

      swal("Success", "Deletion request sent successfully!", "success");
    } catch (error) {
      console.error(error);
      swal("Error", "Failed to send deletion request", "error");
    }
  };

  if (campusLoading || allModulesLoading || Isload) {
    return ( 
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
         <Loader />
        </div>
      </AdminLayout>
    );
  }

  if (campusError || allModulesError) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen text-center text-red-500">
          ⚠️ Error loading modules. Please try again later.
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <NetworkIndicator />
      <div className="p-10 bg-white min-h-screen">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.history.back()}
              className="flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 bg-white text-slate-600 hover:text-slate-800 hover:border-slate-300 shadow-sm"
            >
              <IoMdArrowRoundBack className="w-5 h-5" />
            </button>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Campus Modules</div>
              <div className="text-2xl font-extrabold text-slate-900 max-md:text-xl">
                {campusName}
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="w-full max-w-sm">
            <div className="relative">
              <input
                type="text"
                placeholder="Search modules by name"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Module Listing */}
        {filteredModules.length === 0 ? (
          <div className="mt-10 text-center text-gray-500">
            🚫 No modules found matching your search.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredModules.map((module) => {
              const totalDays = module.details?.daywise_plan?.length || 0;
              const enabledDates = module.enabled_dates || [];
              const moduleDetails = { ...module.details, enabled_dates: enabledDates };

              const totalQuestions = Array.isArray(module.details?.daywise_plan)
                ? module.details.daywise_plan.reduce((sum, day) => sum + (Array.isArray(day.questions) ? day.questions.length : 0), 0)
                : 0;

              return (
                <div
                  key={module.moduleID}
                  onClick={() => {
                    setSelectedModule(module);
                    setSelectedDates([]);
                  }}
                  className="group relative flex flex-col h-64 p-6 rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-slate-900 leading-tight truncate group-hover:text-[#0e3d5d]">
                        {module.details?.name || "Unnamed Module"}
                      </h3>
                    </div>
                    <span className="ml-auto px-2.5 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-full">
                      {module.details?.domain || "General"}
                    </span>
                  </div>

                  <p className="text-sm text-slate-600 line-clamp-3">
                    {module.details?.short_description || "No description"}
                  </p>

                  <div className="mt-auto pt-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                        <div className="text-[11px] uppercase tracking-wider text-slate-400">Questions</div>
                        <div className="text-lg font-bold text-slate-900">{totalQuestions}</div>
                      </div>
                      <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                        <div className="text-[11px] uppercase tracking-wider text-slate-400">Clusters</div>
                        <div className="text-lg font-bold text-slate-900">{module.clusterCount}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Date filter modal */}
      <ModalUI
        open={!!selectedModule}
        heading="Choose date"
        subtitle="Choose your date for loading module report"
        primaryButtonText="Apply"
        secondaryButtonText="Cancel"
        onClose={() => setSelectedModule(null)}
        onConfirm={() => {
          if (selectedModule) {
            const state = {
              name: {
                ...selectedModule.details,
                enabled_dates: selectedModule.enabled_dates,
              },
              selectedDayIndexes: selectedDates,
            };
            navigate(`/admin/reports/module/${campusName}/${selectedModule.moduleID}`, { state });
          }
        }}
        showOverlay
      >
        <div style={{ marginTop: 22, textAlign: "left" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              marginBottom: 8,
              borderRadius: 14,
              background: "#eef2ff",
              border: "1px solid #c7d2fe",
              fontSize: 13,
              fontWeight: 700,
              color: "#4338ca",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={
                selectedModule &&
                selectedDates.length === selectedModule.details?.daywise_plan?.length
              }
              onChange={() => {
                if (!selectedModule) return;
                const days = selectedModule.details?.daywise_plan || [];
                if (selectedDates.length === days.length) {
                  setSelectedDates([]);
                } else {
                  setSelectedDates(days.map((_, idx) => idx));
                }
              }}
              style={{ accentColor: "#4338ca", width: 16, height: 16 }}
            />
            Select All
          </label>
          <div
            style={{
              maxHeight: 260,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              paddingRight: 4,
            }}
          >
            {selectedModule?.details?.daywise_plan?.map((day, idx) => {
              const rawDate = selectedModule?.enabled_dates?.[idx];
              const formattedDate = rawDate
                ? new Date(rawDate).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : `Day ${idx + 1}`;
              return (
                <label
                  key={day.day_id || idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    borderRadius: 12,
                    background: selectedDates.includes(idx) ? "#f0f4ff" : "#f8fafc",
                    border: `1px solid ${selectedDates.includes(idx) ? "#a5b4fc" : "#eef2f7"}`,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedDates.includes(idx)}
                    onChange={() => {
                      setSelectedDates(prev =>
                        prev.includes(idx)
                          ? prev.filter(d => d !== idx)
                          : [...prev, idx]
                      );
                    }}
                    style={{ accentColor: "#4338ca", width: 16, height: 16 }}
                  />
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: "#1e293b" }}>
                    {formattedDate}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      </ModalUI>
    </AdminLayout>
  );
};

export default ModuleListing;
